<template>
  <div class="asset-library">
    <div class="al-left">
      <el-tabs :value="activeKind" class="al-kind-tabs" @tab-click="handleTabClick">
        <el-tab-pane v-for="k in kinds" :key="k" :label="kindLabel(k)" :name="k" />
      </el-tabs>
      <AssetCategoryTree
        :categories="categories"
        :uncategorized-count="uncategorizedCount"
        :total-all="totalAll"
        :value="categoryId"
        :editable="canWrite"
        :kind="activeKind"
        :scope="scope"
        :game-id="scope === 'book' ? gameId : 0"
        @input="selectCategory"
        @changed="reload"
      />
    </div>

    <div class="al-right">
      <div class="al-toolbar">
        <el-input
          v-model="keyword"
          placeholder="搜索名称"
          prefix-icon="el-icon-search"
          size="small"
          clearable
          class="al-search"
        />
        <!-- A 计划 §5.3：管理员「个人素材管理」——进入后范围按钮组收起，换成账号下拉 + 标题 + 返回 -->
        <div v-if="userView.active" class="al-user-bar">
          <el-select
            :value="userView.userId || undefined"
            size="small"
            class="al-user-select"
            data-test="asset-user-select"
            placeholder="选择账号"
            @change="selectOwner"
          >
            <el-option v-for="o in owners" :key="o.user_id" :value="o.user_id" :label="`${o.username}（${o.count}）`" />
          </el-select>
          <span v-if="userView.username" class="al-user-title" data-test="asset-user-title">{{ userView.username }} 的素材</span>
          <el-button size="small" data-test="asset-user-back" @click="exitUserView">返回个人素材</el-button>
        </div>
        <div v-else class="al-scope-bar">
          <el-radio-group :value="scope" size="small" @input="handleScopeChange">
            <el-radio-button label="official">官方素材</el-radio-button>
            <el-radio-button label="mine">个人素材</el-radio-button>
            <el-radio-button v-if="gameId > 0" label="book">本书素材</el-radio-button>
          </el-radio-group>
          <el-button v-if="canManageUsers" size="small" data-test="asset-user-manage" @click="enterUserView">个人素材管理</el-button>
        </div>
      </div>

      <!-- 上传条对 manage/pick 两种模式都开（2026-10-09 用户要求：在角色管理等场景进素材
           选择器后可直接上传，不用先跑去素材库传完再回来选）；批量移动/删除仍是 manage 专属——
           pick 模式的 selectedIds 是「待选的素材」，跟批量管理混在一个条里会误删 -->
      <div v-if="canWrite" class="al-manage-bar">
        <AssetUploadButton
          :kind="activeKind"
          :scope="scope"
          :game-id="scope === 'book' ? gameId : 0"
          :target-category-id="uploadTargetCategoryId"
          :blocked-reason="uploadBlockedReason"
          @uploaded="handleUploaded"
        />
        <template v-if="mode === 'manage'">
          <el-button size="small" :disabled="selectedIds.length === 0" @click="openBatchMove">批量移动</el-button>
          <el-button size="small" type="danger" plain :disabled="selectedIds.length === 0" @click="handleBatchDelete">
            批量删除
          </el-button>
          <span v-if="selectedIds.length" class="al-selected-count">已选 {{ selectedIds.length }} 项</span>
        </template>
      </div>

      <div class="al-content" @scroll="onScroll">
        <div v-if="!isAudio" class="al-grid">
          <div
            v-for="a in items"
            :key="a.id"
            class="al-card"
            :class="{ 'is-selected': isSelected(a.id), 'is-pickable': mode === 'pick' }"
            @click="onCardClick(a)"
          >
            <div class="al-card-thumb">
              <img :src="a.url" :alt="a.name" class="al-thumb-img" loading="lazy">
              <div class="al-zoom-btn" :title="'查看原图：' + a.name" @click.stop>
                <el-image :src="a.url" :preview-src-list="[a.url]" class="al-zoom-trigger" />
                <i class="el-icon-zoom-in"></i>
              </div>
              <div v-if="showManageBar" class="al-card-check" @click.stop="toggleSelected(a)">
                <el-checkbox :value="isSelected(a.id)" />
              </div>
              <div v-if="showManageBar" class="al-card-actions">
                <span @click.stop="handleRenameAsset(a)">改名</span>
                <span @click.stop="handleMoveAsset(a)">移动</span>
                <span class="is-danger" @click.stop="handleDeleteAsset(a)">删除</span>
              </div>
            </div>
            <div class="al-card-name" :title="a.name">{{ a.name }}</div>
            <div v-if="scope === 'book'" class="al-card-uploader" :title="uploaderName(a.user_id)">{{ uploaderName(a.user_id) }}</div>
          </div>
        </div>

        <el-table
          v-else
          ref="assetTable"
          :data="items"
          row-key="id"
          size="small"
          :row-class-name="audioRowClassName"
          @selection-change="onTableSelectionChange"
        >
          <el-table-column v-if="showManageBar" type="selection" width="46" reserve-selection />
          <el-table-column label="名称" min-width="200">
            <template slot-scope="scope">
              <span
                class="al-audio-name"
                :class="{ 'is-readonly': !nameClickable }"
                :title="scope.row.name"
                @click="onNameClick(scope.row)"
              >
                {{ scope.row.name }}
              </span>
              <!-- 本书素材库是共享库，音频也要标出是谁传的：不单独起一列（团队 2026-09-19 定案，
                   这张表的列宽预算本就贴着弹窗可用宽度，见下方「播放」列的说明，容不下再加一列），
                   跟图片卡片一样挪进名称下面第二行，只占行高、不挤宽度。
                   这里必须用 isBookScope 计算属性、不能写 scope === 'book'——本层 template 的
                   slot-scope="scope" 会把组件自己的 scope prop 遮蔽掉，写了不报错但恒为 false
                   （比较的是行数据对象），这一行会永远不出现，且没有任何报错提示（团队 2026-09-19
                   review 发现，上一版正是这么错的） -->
              <div v-if="isBookScope" class="al-audio-uploader" :title="uploaderName(scope.row.user_id)">
                {{ uploaderName(scope.row.user_id) }}
              </div>
            </template>
          </el-table-column>
          <el-table-column label="时长" width="90">
            <template slot-scope="scope">{{ formatDuration(scope.row.duration_sec) }}</template>
          </el-table-column>
          <!-- 400 不是随手写的：Chrome 原生 audio 的播放键/时间/音量/菜单是固定尺寸，
               合计约 240px，进度条只能吃剩下的空间。列宽 240 时播放器实测只有 220px，
               进度条被压成一小截拖不动；给到 400 才留得下约 140px 的进度条 -->
          <el-table-column label="播放" width="400">
            <template slot-scope="scope">
              <audio :src="scope.row.url" controls preload="none" class="al-audio-player" @play="handleAudioPlay" />
            </template>
          </el-table-column>
          <!-- pick 模式的选择入口（用户 2026-09-08 要求）：原来靠点名称链接选中，看起来像在点一个链接；
               改成一列明确的按钮，名称列在 pick 模式下退回普通文字。列宽 100 是算过的：弹窗 1210 减
               el-dialog 左右 padding 40、左栏 330、内容区 padding 32 后表格约 808px，名称 200 + 时长 90
               + 播放 400 + 本列 100 = 790，再宽就会冒横向滚动条（2026-10-16 左栏 220→330 时弹窗跟着
               1100→1210，就是为了让这 808px 不变） -->
          <el-table-column v-if="mode === 'pick'" label="操作" width="100" align="center">
            <template slot-scope="scope">
              <el-button
                size="mini"
                type="warning"
                :plain="!(multiple && isSelected(scope.row.id))"
                class="al-pick-btn"
                @click="onPick(scope.row)"
              >{{ multiple && isSelected(scope.row.id) ? '取消选择' : '选择此音频' }}</el-button>
            </template>
          </el-table-column>
          <el-table-column v-if="showManageBar" label="操作" width="60" align="center">
            <template slot-scope="scope">
              <el-dropdown trigger="click" @command="onRowCommand">
                <span class="al-row-more"><i class="el-icon-more"></i></span>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item :command="{ action: 'rename', asset: scope.row }">改名</el-dropdown-item>
                  <el-dropdown-item :command="{ action: 'move', asset: scope.row }">移动</el-dropdown-item>
                  <el-dropdown-item :command="{ action: 'delete', asset: scope.row }">删除</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>

        <div v-if="userView.active && userView.userId === 0" class="al-status-text" data-test="asset-user-empty">暂无其他账号的个人素材</div>
        <div v-else-if="loading" class="al-status-text">加载中…</div>
        <div v-else-if="items.length === 0 && noMore" class="al-status-text">
          {{ isAudio ? '暂无音频' : '暂无素材' }}
        </div>
        <div v-else-if="noMore && items.length > 0" class="al-status-text al-status-end">没有更多了</div>
      </div>

      <div v-if="mode === 'pick' && multiple" class="al-confirm-bar">
        <span>已选 {{ selectedIds.length }} 项</span>
        <el-button type="primary" size="small" :disabled="selectedIds.length === 0" @click="confirmPickMany">
          确定
        </el-button>
      </div>
    </div>

    <!-- 单条移动（handleMoveAsset）与批量移动（openBatchMove）共用同一个分类选择弹窗，
         用 movePickerIsBatch 区分提交时走 updateAsset 还是 batchAssets -->
    <el-dialog :title="movePickerTitle" :visible.sync="movePickerVisible" width="360px" append-to-body>
      <!-- 素材只能挂在叶子分组（spec §6.4）：只列叶子，label 用完整路径，两个父下的同名叶子才分得清 -->
      <el-select v-model="movePickerCategoryId" placeholder="请选择分类" class="al-move-select">
        <el-option :value="null" label="未分类" />
        <el-option v-for="o in moveTargetOptions" :key="o.id" :value="o.id" :label="o.label" />
      </el-select>
      <span slot="footer">
        <el-button @click="movePickerVisible = false">取消</el-button>
        <el-button type="primary" :loading="movePickerSubmitting" @click="confirmMovePicker">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
// $prompt 的类型签名跟 $confirm/$alert 共用同一个 Promise<MessageBoxData>，TS 分不出 $prompt
// 实际总是 resolve 成带 value 字段的那支，要读 .value 就得显式转成 MessageBoxInputData
import type { MessageBoxInputData } from 'element-ui/types/message-box'
import type { AssetKind, AssetScope, Asset, AssetCategoryView, AssetListData, AssetBatchData, AssetOwner } from '@/api/asset'
import { listAssets, listAssetOwners, updateAsset, deleteAsset, batchAssets } from '@/api/asset'
import AssetUploadButton from './AssetUploadButton.vue'
import AssetCategoryTree from './AssetCategoryTree.vue'
import { categoryLeaves, categoryPathLabel, uploadTarget } from '@/utils/assetCategoryHierarchy'

/** 页签展示文案，与后端 db.AssetKinds 六个取值一一对应；具体展示哪几个由父组件传入的 kinds 决定（W2） */
const KIND_LABELS: Record<AssetKind, string> = {
  bg: '背景图',
  portrait: '立绘',
  avatar: '头像',
  item: '通用物品',
  bgm: '背景音乐',
  sfx: '音效'
}

// 素材名后端只限空/255 字符（asset_validate.go ValidateAssetName），可含任意字符；
// showBatchResult 把它拼进 dangerouslyUseHTMLString 的 $alert 时必须先转义，否则名字里
// 塞 HTML/脚本会被当成标签执行（2026-09-01 最终审查 I3，主要是 self-XSS，但官方库素材名
// 可由另一个 admin 起，存在跨用户路径）
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

interface RowCommand {
  action: 'rename' | 'move' | 'delete'
  asset: Asset
}

@Component({ components: { AssetUploadButton, AssetCategoryTree } })
export default class AssetLibrary extends Vue {
  @Prop({ required: true }) readonly kinds!: AssetKind[]
  @Prop({ required: true }) readonly mode!: 'manage' | 'pick'
  @Prop({ default: false }) readonly multiple!: boolean
  // book 页签（本书素材：该书的共享库，凡能进这本书的人都能读写）依赖它——有 game_id 才渲染
  // 该页签、请求 scope==='book' 时才带上；六项 D 计划 F2 起把真实 game_id 贯穿传入，
  // 无 gameId 时 book 页签不显示是预期（保持旧行为）。2026-09-18 本书素材库改造起该库不再
  // 只读，见下方 canWrite
  @Prop({ default: 0 }) readonly gameId!: number
  /** 初始落到的范围页签。默认 'book'（2026-10-16 用户要求：素材管理 / 音频管理和各处选素材
      弹窗都先落在本书素材；此前只有角色管理的「更换立绘」是，2026-10-09）——管理弹窗里上传
      默认进的是当前页签的库，选素材弹窗要是还停在个人素材，刚传的找不到、每次都得切。
      gameId 无效（没有 book 页签）时在 created() 里回落 'mine'，不能让作者落在页签按钮都
      看不见的范围里 */
  @Prop({ default: 'book' }) readonly defaultScope!: AssetScope

  activeKind: AssetKind = 'bg' // created() 里会立刻改写成 kinds[0]，这里只是满足字段初始化
  scope: AssetScope = 'mine' // created() 里按 defaultScope 改写，这里只是满足字段初始化；官方库只读（W4）
  /** 「个人素材管理」视图（A 计划 §5.3）：管理员查看指定账号的个人素材，只读可选用。
      `active` 时范围按钮组收起、请求走 scope=user；`userId===0` 是"账号列表为空"的空态 */
  userView: { active: boolean; userId: number; username: string } = { active: false, userId: 0, username: '' }
  /** 当前 kind 下有个人素材的账号列表（`listAssetOwners`），进入 user 视图 / 切 kind 时刷新 */
  owners: AssetOwner[] = []
  // undefined=全部, 0=未分类, 其他=分类 id。
  // 初始化必须放在 data() 钩子里，不能写成 `categoryId: number|undefined = undefined`：
  // vue-class-component@7.2.6 的 collectDataFromConstructor 收集类属性时是
  // `Object.keys(data).forEach(k => { if (data[k] !== undefined) plainData[k] = data[k] })`
  // （node_modules/vue-class-component/dist/vue-class-component.common.js:170-174），
  // 值为 undefined 的类属性会被直接丢弃、根本不进 data，因而不是响应式的。
  // 而本字段的「全部」态恰好就是 undefined，模板里又有 :value="categoryId" 绑到分类树上。
  // 此前之所以看起来正常，只是因为切分类必调 reload()、reload 会改动 loading/items 等
  // 响应式字段顺带带动重渲染把它盖住了——一旦将来让切分类不再发请求（比如加客户端缓存），
  // 分类高亮就会静默失效且极难查。B端02 的 VariableManagerDialog 因为切种类不发请求，
  // 已经踩到过这个坑的真实后果。
  categoryId!: number | undefined
  keyword = ''
  items: Asset[] = []
  categories: AssetCategoryView[] = []
  // 本书素材库下卡片/音频表要显示「谁传的」：key 是 user_id 的字符串形式，值来自列表接口
  // 顺带下发的 uploaders 映射（仅 scope==='book' 时非空，见 AssetListData.uploaders 的注释）
  uploaderNames: Record<string, string> = {}
  uncategorizedCount = 0
  totalAll = 0
  total = 0
  page = 1
  size = 40
  loading = false
  noMore = false
  selectedIds: number[] = [] // manage 模式的批量勾选 / pick 多选，跨页签保留（reload 不清空，见下）
  selectedMap: Record<number, Asset> = {} // 配合 selectedIds 保留完整 Asset 对象，供 pick-many 事件使用

  // 移动分类弹窗：单条移动（handleMoveAsset）与批量移动（openBatchMove）共用，
  // movePickerIsBatch 区分提交时调 updateAsset 还是 batchAssets
  movePickerVisible = false
  movePickerCategoryId: number | null = null // null = 未分类
  movePickerTargetIds: number[] = []
  movePickerIsBatch = false
  movePickerSubmitting = false

  searchTimer: number | null = null

  // 见上方 categoryId 的注释：类属性初值为 undefined 会被 vue-class-component 丢弃，
  // 而 data() 返回对象的每个键都会被 defineReactive，undefined 值也不例外
  data(): { categoryId: number | undefined } {
    return { categoryId: undefined }
  }

  get isAudio(): boolean {
    return this.activeKind === 'bgm' || this.activeKind === 'sfx'
  }

  // 音频表「名称」列的 slot-scope="scope" 会遮蔽掉本组件的 scope prop，模板里不能直接写
  // scope === 'book'（比较的会是行数据对象，恒为 false，且不报错——团队 2026-09-19 review
  // 发现），改用这个计算属性
  get isBookScope(): boolean {
    return this.scope === 'book'
  }

  get isAdmin(): boolean {
    return this.$store.state.user.user?.role === 'admin'
  }

  /** 「个人素材管理」入口只给管理员、且只在「个人素材」范围下出现（A 计划 §5.3） */
  get canManageUsers(): boolean {
    return this.isAdmin && this.scope === 'mine'
  }

  /** 请求真正用的范围：user 视图下恒为 'user'，否则就是按钮组选的 scope */
  get requestScope(): AssetScope {
    return this.userView.active ? 'user' : this.scope
  }

  // 官方库下非 admin 一律只读：隐藏上传/批量/改名/删除/分类编辑（W4）。
  // book（本书素材：该书的共享库）不再是只读库（2026-09-18 本书素材库改造）——凡能进这本书
  // 的人（作者、管理员、编辑）都能上传和使用，写权限由后端按书级鉴权兜底，这里只要求
  // gameId 有效即可放行前端入口，不区分角色。
  // user 视图（管理员看别人的个人库，A 计划）同样恒只读：只看 + 选用（用户定案）。
  get canWrite(): boolean {
    if (this.userView.active) return false
    if (this.scope === 'mine') return true
    if (this.scope === 'book') return this.gameId > 0
    return this.isAdmin
  }

  /** 上传目标（作者定案 2026-09-18）：选中叶子分组就落在它上面，选中「全部」/「未分类」进未分类 */
  get uploadTargetCategoryId(): number | null {
    return uploadTarget(this.categories, this.categoryId).categoryId
  }

  /** 选中的分组有子分组时的拦截文案（空串 = 不拦）。带上分组名，作者才知道该去哪儿重选 */
  get uploadBlockedReason(): string {
    if (!uploadTarget(this.categories, this.categoryId).blocked) return ''
    const c = this.categories.find(x => x.id === this.categoryId)
    return c
      ? `「${c.name}」下面还有子分组，素材只能上传到没有子分组的分组里`
      : '素材只能上传到没有子分组的分组里，请先选中一个'
  }

  /** 「移动到分类」的可选目标：只有叶子分组，label 是「背景 / 校园 / 教室」式的路径（spec §6.4） */
  get moveTargetOptions(): Array<{ id: number; label: string }> {
    return categoryLeaves(this.categories).map(c => ({ id: c.id, label: categoryPathLabel(this.categories, c.id) }))
  }

  get movePickerTitle(): string {
    return this.movePickerIsBatch
      ? `批量移动到分类（${this.movePickerTargetIds.length} 个）`
      : '移动到分类'
  }

  get showManageBar(): boolean {
    return this.mode === 'manage' && this.canWrite
  }

  // 音频名称是否表现为「可点」（链接色 + pointer）：只有 manage 模式下点它是改名动作，且只有
  // canWrite 时才可点——官方库下非 admin 不能让名称看起来能点、点了才靠后端 403 兜底
  // （2026-09-01 最终审查 I1）。pick 模式下名称是普通文字，选中走「选择此音频」按钮
  // （用户 2026-09-08 要求，之前是点名称即选中）
  get nameClickable(): boolean {
    return this.mode === 'manage' && this.canWrite
  }

  get selectedAssetList(): Asset[] {
    return this.selectedIds.map(id => this.selectedMap[id]).filter((a): a is Asset => !!a)
  }

  // 搜索框防抖 300ms 再 reload，不要每敲一个字发一次请求
  @Watch('keyword')
  onKeywordChange() {
    if (this.searchTimer !== null) window.clearTimeout(this.searchTimer)
    this.searchTimer = window.setTimeout(() => {
      this.searchTimer = null
      this.reload()
    }, 300)
  }

  created() {
    this.activeKind = this.kinds[0]
    // defaultScope 生效（见 prop 注释）：book 需要有效 gameId 才有页签，否则回落 mine
    this.scope = this.defaultScope === 'book' && this.gameId <= 0 ? 'mine' : this.defaultScope
    this.fetch()
  }

  beforeDestroy() {
    if (this.searchTimer !== null) window.clearTimeout(this.searchTimer)
  }

  kindLabel(k: AssetKind): string {
    return KIND_LABELS[k] || k
  }

  // 本书素材库下卡片/音频表「上传者」的展示名，三条分支：
  // ① uploaders 映射里查到真名 → 用户名；
  // ② 查不到、且 id 是 -1（存量迁移：老官方库素材认回本书时 user_id 原样保留成 -1，
  //    不是真实用户——gal_test 里这样的素材有 797 条、分布在 28 本书，迁移跑完后多数
  //    书的本书素材库里绝大部分素材都是这个 id）→ 显示「历史素材」，不能让它们整齐地
  //    显示成「用户#-1」误导成 bug（团队 2026-09-19 review 发现）；
  // ③ 查不到、且 id 不是 -1（用户名解析失败，或还没翻到那一页）→ 兜底「用户#<id>」，
  //    同 A 计划账号列表一路的兜底约定
  uploaderName(userId: number): string {
    const name = this.uploaderNames[String(userId)]
    if (name) return name
    if (userId === -1) return '历史素材'
    return `用户#${userId}`
  }

  handleTabClick(tab: { name?: string }) {
    const k = tab.name as AssetKind
    if (!k || this.activeKind === k) return
    // manage 模式下跨页签/跨库勾选是作者看不见、也清不掉的陷阱——切页签前清空
    // （pick 多选跨页签保留是合理需求，不受影响，见下方 reload 注释）
    // （2026-09-01 最终审查 I2）；放在 this.activeKind 赋值之前，让 clearSelection()
    // 内部对 isAudio 的判断用的是切换前的 kind，才能正确清掉切换前那张音频表格的勾选
    if (this.mode === 'manage') this.clearSelection()
    this.activeKind = k
    // 换页签后分类筛选回到全部（分类按 (user, kind) 归属，旧 kind 的分类 id 在新 kind 下
    // 不存在：带着它查必然是空网格 + 分类树整棵树没有高亮，且 AssetCategoryTree 的
    // @Watch('categories') 只同步本地副本、不复位选中值，不会自愈）。放在两条分支之前，
    // user 视图与普通视图都生效——同 handleScopeChange / exitUserView 的既有一行（终审 F3）
    this.categoryId = undefined
    if (this.userView.active) {
      this.loadOwners(true) // 账号列表按 kind 算，换页签要重拉；当前账号还在就保留，否则选第一个
      return
    }
    this.reload()
  }

  handleScopeChange(val: AssetScope) {
    if (this.scope === val) return
    // 同上：admin 在 mine↔official 之间切换时同样要清空 manage 模式的选中态（I2）
    if (this.mode === 'manage') this.clearSelection()
    this.scope = val
    this.categoryId = undefined // 换库后分类筛选回到全部（不同库的分类 id 不通用）
    this.reload()
  }

  /** 进入「个人素材管理」（A 计划 §5.3）：清选中、清列表、拉账号列表（拉到就选第一个并加载） */
  async enterUserView() {
    if (this.mode === 'manage') this.clearSelection()
    this.userView = { active: true, userId: 0, username: '' }
    this.resetListForUserView()
    await this.loadOwners(false)
  }

  /** 退出 user 视图：scope 保持 'mine' 不变（入口本来就只在 mine 下），重拉自己的库 */
  exitUserView() {
    if (this.mode === 'manage') this.clearSelection()
    this.userView = { active: false, userId: 0, username: '' }
    this.owners = []
    this.categoryId = undefined // 同上：别人库的分类 id 在自己的库里不通用，回到全部
    this.reload()
  }

  /** 账号下拉选中：换账号即重拉列表（分类树、计数都随响应更新） */
  selectOwner(userId: number) {
    const o = this.owners.find(x => x.user_id === userId)
    if (!o || this.userView.userId === userId) return
    if (this.mode === 'manage') this.clearSelection()
    this.userView = { active: true, userId: o.user_id, username: o.username }
    this.categoryId = undefined // 换账号 = 换库，分类筛选回到全部（不同库的分类 id 不通用）
    this.reload()
  }

  /**
   * 拉当前 kind 下有个人素材的账号。`keepCurrent`：当前账号仍在新列表里就保留，否则选第一个；
   * 列表为空 → 空态（`userId=0`，不发列表请求，模板显示「暂无其他账号的个人素材」）。
   * 响应回来时 kind 已换 / 已退出 user 视图 → 丢弃（同 `fetch()` 的 stale 纪律）。
   */
  private async loadOwners(keepCurrent: boolean) {
    const requestKind = this.activeKind
    try {
      const res = await listAssetOwners(requestKind)
      if (requestKind !== this.activeKind || !this.userView.active) return
      if (res.data.code !== 0) {
        this.$message.error(res.data.message || '加载账号列表失败')
        this.owners = []
        this.userView = { active: true, userId: 0, username: '' }
        this.resetListForUserView()
        return
      }
      const items = ((res.data.data && res.data.data.items) || []) as AssetOwner[]
      this.owners = items
      const cur = keepCurrent ? items.find(x => x.user_id === this.userView.userId) : undefined
      const pick = cur || items[0]
      if (!pick) {
        this.userView = { active: true, userId: 0, username: '' }
        this.resetListForUserView()
        return
      }
      this.userView = { active: true, userId: pick.user_id, username: pick.username }
      this.reload()
    } catch (e) {
      // 网络层错误已由全局拦截器弹窗兜底；停在空态，可点「返回个人素材」
      this.owners = []
      this.userView = { active: true, userId: 0, username: '' }
      this.resetListForUserView()
    }
  }

  /** user 视图空态 / 切换前把列表相关状态清干净，不残留上一个账号（或自己的库）的卡片、分类与分类筛选 */
  private resetListForUserView() {
    this.items = []
    this.categories = []
    this.categoryId = undefined // 分类筛选回到全部：带着自己库的分类 id 去查别人的库会筛出空列表，且分类树里没有对应项高亮
    this.uncategorizedCount = 0
    this.totalAll = 0
    this.total = 0
    this.uploaderNames = {} // user 视图恒不是 book，不会用到，但一并清空避免残留旧库的映射
    this.page = 1
    this.noMore = true
  }

  selectCategory(id: number | undefined) {
    if (this.categoryId === id) return
    this.categoryId = id
    this.reload()
  }

  // 切页签 / 切库 / 切分类 / 改关键词都回到第一页重拉；reload() 本身不清空 selectedIds——
  // 切分类、改关键词这两种不会让"已选中的 id"失去意义（还是同一个 kind/scope 下的素材，
  // 批量操作依然安全），所以继续保留。切页签（handleTabClick）和切库（handleScopeChange）
  // 不一样：kind/scope 一变，之前选中的 id 可能已经不属于当前筛选范围了，manage 模式下这两
  // 个入口会在调用 reload() 之前先 clearSelection()（I2，2026-09-01 最终审查）；pick 多选允
  // 许跨页签/跨库选择是产品要的行为，不受影响，这两个入口只在 mode === 'manage' 时才清
  reload() {
    this.page = 1
    this.items = []
    this.noMore = false
    this.fetch()
  }

  // AssetUploadButton（T4）上传成功后的回调：整表用 reload() 从服务端刷新一遍即可——
  // 分类计数、未分类数、总数都在这一步一起更新。后端 ListAssets 是 id DESC 排序
  // （ai-game-backend-go/internal/db/asset.go:101），新素材 id 最大，reload() 回来后
  // 天然就在第一页最前面，不需要本地 unshift 插队；unshift 也起不到效果——reload()
  // 会同步把 items 清空成 []，插入的内容在 Vue 渲染前就被这一步覆盖掉了。
  handleUploaded() {
    this.reload()
  }

  async fetch() {
    if (this.loading || this.noMore) return
    if (this.userView.active && this.userView.userId === 0) return // 空态：没有可看的账号，不发列表请求
    this.loading = true
    // 发起请求时的筛选上下文；响应回来后如果这些已经变了（用户中途切了页签/库/分类/
    // 关键词/书），说明这次响应已经过期，不能再写进 state——否则会出现"页签已经切到立绘，
    // 网格却还在显示背景图"的错位（Important #1，2026-09-01 审查发现）
    const requestKind = this.activeKind
    const requestScope = this.requestScope
    const requestUserId = this.userView.userId
    const requestCategoryId = this.categoryId
    const requestKeyword = this.keyword
    const requestGameId = this.gameId
    let stale = false
    try {
      const res = await listAssets({
        kind: requestKind,
        scope: requestScope,
        // book 必须带 game_id（后端按此解析该书的共享素材库）；非 book 不传，undefined 时 axios 不会带上这个参数
        game_id: requestScope === 'book' ? requestGameId : undefined,
        user_id: requestScope === 'user' ? requestUserId : undefined, // user 必须带 user_id（仅管理员）；非 user 不传
        category_id: requestCategoryId, // undefined 时 axios 不会带上这个参数
        q: requestKeyword.trim() || undefined,
        page: this.page,
        size: this.size
      })
      stale =
        requestKind !== this.activeKind ||
        requestScope !== this.requestScope ||
        requestCategoryId !== this.categoryId ||
        requestKeyword !== this.keyword ||
        requestGameId !== this.gameId ||
        requestUserId !== this.userView.userId
      if (stale) return
      if (res.data.code !== 0) {
        this.$message.error(res.data.message || '加载素材失败')
        return
      }
      const d = res.data.data as AssetListData
      // 分类计数与总数不受 q / category_id 影响，每次都用最新的覆盖（W3）
      this.categories = d.categories || []
      this.uncategorizedCount = d.uncategorized_count
      this.totalAll = d.total_all
      this.total = d.total
      this.items = this.page === 1 ? (d.items || []) : this.items.concat(d.items || [])
      // 同 items 的翻页纪律：第一页整体替换（换库/换关键词/reload 都会先回到第一页，旧库的
      // 映射不该残留），后续分页累加合并（uploaders 只覆盖"这一页"出现过的 user_id）
      this.uploaderNames = this.page === 1 ? (d.uploaders || {}) : { ...this.uploaderNames, ...(d.uploaders || {}) }
      this.noMore = this.items.length >= d.total
      this.page += 1
    } finally {
      this.loading = false
      // 上下文切换时发生的那次 reload() 已经把 page/items/noMore 复位好了，只是它内部的
      // fetch() 被这次仍在途的旧请求用 loading 挡了下来（第一行）；这里 loading 已经复位、
      // 上下文也已经是最新的，补发一次，保证切换后一定能加载到新数据，不用用户再点一次
      if (stale) this.fetch()
    }
  }

  // 内容区触底继续加载（W5：滚动加载，不做分页条）
  onScroll(e: Event) {
    const el = e.target as HTMLElement
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) this.fetch()
  }

  isSelected(id: number): boolean {
    return this.selectedIds.indexOf(id) >= 0
  }

  // 音频表格：图片网格靠 al-card 的 is-selected 描边给选中反馈（62 行），
  // 音频这边 pick+multiple 模式没有 checkbox 列（manage 模式已经有 el-table 原生的
  // selection-row 高亮），所以用 row-class-name 给整行加一个高亮 class，视觉上跟卡片的
  // 靛紫呼应；「选择此音频」按钮在多选态下同时切成实心作第二重反馈
  audioRowClassName({ row }: { row: Asset }): string {
    return this.mode === 'pick' && this.isSelected(row.id) ? 'al-row-selected' : ''
  }

  // 图片网格的批量勾选（manage 模式）与多选点选（pick 模式）共用同一份 selectedIds
  toggleSelected(a: Asset) {
    const i = this.selectedIds.indexOf(a.id)
    if (i >= 0) {
      this.selectedIds.splice(i, 1)
    } else {
      this.selectedIds.push(a.id)
      this.selectedMap[a.id] = a
    }
  }

  // el-table 的多选列开了 reserve-selection + row-key，Element 自己维护跨页签/跨页的选中集合，
  // 这里收到的 rows 就是当前完整选中结果，直接镜像进 selectedIds/selectedMap 即可
  onTableSelectionChange(rows: Asset[]) {
    this.selectedIds = rows.map(r => r.id)
    rows.forEach(r => {
      this.selectedMap[r.id] = r
    })
  }

  onCardClick(a: Asset) {
    if (this.mode === 'pick') this.onPick(a)
  }

  onNameClick(a: Asset) {
    // pick 模式下名称不是选中入口（2026-09-08 起选中走「选择此音频」按钮）；manage 模式下官方库非 admin
    // 只读：不能靠后端 403 兜底，入口本身要隐藏（W4，2026-09-01 最终审查 I1）
    if (!this.nameClickable) return
    this.handleRenameAsset(a)
  }

  onPick(a: Asset) {
    if (this.mode !== 'pick') return
    if (this.multiple) {
      this.toggleSelected(a)
      return
    }
    this.$emit('pick', a)
  }

  confirmPickMany() {
    this.$emit('pick-many', this.selectedAssetList)
  }

  // duration_sec 保留两位小数直接显示，如 5.42、30.53（W12），为 null 显示 -
  formatDuration(sec: number | null): string {
    return sec === null || sec === undefined ? '-' : sec.toFixed(2)
  }

  // 音频试听互斥（2026-10-09 用户要求）：表格里每个音频是独立的原生 audio 控件，作者连着
  // 试听几个时旧的会继续出声；新起播一个就把本组件里其余播放器全暂停（含已播完的，pause
  // 对它们是无害空转）
  handleAudioPlay(e: Event) {
    const players = (this.$el as HTMLElement).querySelectorAll<HTMLAudioElement>('.al-audio-player')
    players.forEach(p => {
      if (p !== e.target) p.pause()
    })
  }

  onRowCommand(cmd: RowCommand) {
    if (cmd.action === 'rename') this.handleRenameAsset(cmd.asset)
    else if (cmd.action === 'move') this.handleMoveAsset(cmd.asset)
    else this.handleDeleteAsset(cmd.asset)
  }

  async handleRenameAsset(a: Asset) {
    try {
      const res = await this.$prompt('素材名称', '改名', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputValue: a.name,
        inputPattern: /\S/,
        inputErrorMessage: '名称不能为空'
      }) as MessageBoxInputData
      const name = res.value.trim()
      if (!name || name === a.name) return
      const ures = await updateAsset(a.id, { name })
      if (ures.data.code !== 0) {
        this.$message.error(ures.data.message || '改名失败')
        return
      }
      this.$message.success('改名成功')
      this.reload()
    } catch (e) {
      // 用户取消，或网络层错误已由全局拦截器弹窗兜底
    }
  }

  // 单条移动只是把弹窗预填成"这一个 id + 它当前的分类"，实际提交跟批量移动走同一个 confirmMovePicker
  handleMoveAsset(a: Asset) {
    this.movePickerTargetIds = [a.id]
    this.movePickerCategoryId = a.category_id
    this.movePickerIsBatch = false
    this.movePickerVisible = true
  }

  async handleDeleteAsset(a: Asset) {
    try {
      await this.$confirm(`确定删除素材「${a.name}」？`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
      const res = await deleteAsset(a.id)
      // 被引用时后端返回 HTTP 200 + code 6004「素材已被使用，不可删除」，不会走 catch，
      // 必须显式判 code，把 message 原样弹给作者，不能因为 HTTP 200 就当成功
      if (res.data.code !== 0) {
        this.$message.error(res.data.message || '删除失败')
        return
      }
      this.$message.success('删除成功')
      this.deselectAsset(a)
      this.reload()
    } catch (e) {
      // 用户取消，或网络层错误已由全局拦截器弹窗兜底
    }
  }

  openBatchMove() {
    if (this.selectedIds.length === 0) return
    this.movePickerTargetIds = this.selectedIds.slice()
    this.movePickerCategoryId = null
    this.movePickerIsBatch = true
    this.movePickerVisible = true
  }

  async confirmMovePicker() {
    const ids = this.movePickerTargetIds
    const categoryId = this.movePickerCategoryId
    this.movePickerSubmitting = true
    try {
      if (this.movePickerIsBatch) {
        const res = await batchAssets({ ids, action: 'move', category_id: categoryId })
        if (res.data.code !== 0) {
          this.$message.error(res.data.message || '批量移动失败')
          return
        }
        this.movePickerVisible = false
        this.showBatchResult(res.data.data as AssetBatchData)
        this.clearSelection()
      } else {
        const res = await updateAsset(ids[0], { category_id: categoryId })
        if (res.data.code !== 0) {
          this.$message.error(res.data.message || '移动失败')
          return
        }
        this.movePickerVisible = false
        this.$message.success('移动成功')
      }
      this.reload()
    } catch (e) {
      // 网络层错误已由全局拦截器弹窗兜底；弹窗保持打开，方便用户重试
    } finally {
      this.movePickerSubmitting = false
    }
  }

  async handleBatchDelete() {
    if (this.selectedIds.length === 0) return
    try {
      await this.$confirm(`确定删除选中的 ${this.selectedIds.length} 个素材？`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
      const ids = this.selectedIds.slice()
      const res = await batchAssets({ ids, action: 'delete' })
      if (res.data.code !== 0) {
        this.$message.error(res.data.message || '批量删除失败')
        return
      }
      this.showBatchResult(res.data.data as AssetBatchData)
      this.clearSelection()
      this.reload()
    } catch (e) {
      // 用户取消，或网络层错误已由全局拦截器弹窗兜底
    }
  }

  // 批量结果必须如实展示，不能只说"操作成功"：后端保证 affected + skipped.length 等于
  // 去重后的 ids 长度，有 skipped 就逐条列出哪个素材、什么原因没处理成功
  showBatchResult(d: AssetBatchData) {
    if (d.skipped && d.skipped.length) {
      const lines = d.skipped.map(s => {
        const a = this.items.find(x => x.id === s.id)
        const name = a ? a.name : '#' + s.id
        // name / reason 都要转义：name 是别人（官方库可能是另一个 admin）起的，
        // reason 虽然目前后端固定返回"素材已被使用"，一并转义无害且更保险
        return `${escapeHtml(name)}：${escapeHtml(s.reason)}`
      })
      this.$alert(lines.join('<br/>'), `成功 ${d.affected} 个，${d.skipped.length} 个未处理`, {
        dangerouslyUseHTMLString: true
      })
    } else {
      this.$message.success(`成功处理 ${d.affected} 个`)
    }
  }

  // 批量操作处理完这批 id 后清空选中态：图片网格靠 selectedIds 驱动，清空即可；
  // 音频表格开了 reserve-selection，内部选中状态是 el-table 自己维护的，
  // 光清空 selectedIds 不会让已勾的行取消勾选，必须调用表格实例的 clearSelection()
  clearSelection() {
    this.selectedIds = []
    if (this.isAudio) {
      const table = this.$refs.assetTable as { clearSelection?: () => void } | undefined
      if (table && table.clearSelection) table.clearSelection()
    }
  }

  // 单条删除成功后，如果这条素材当时也被批量勾选着，要把它从 selectedIds 里摘掉——
  // 不然"已选 N 项"会带着一个已经不存在的 id，batch 接口对不存在的 id 是整批报错
  // （不像批量删除那样局部 skip），会平白导致下一次批量操作失败
  deselectAsset(a: Asset) {
    const i = this.selectedIds.indexOf(a.id)
    if (i < 0) return
    this.selectedIds.splice(i, 1)
    delete this.selectedMap[a.id]
    if (this.isAudio) {
      const table = this.$refs.assetTable as { toggleRowSelection?: (row: Asset, selected?: boolean) => void } | undefined
      if (table && table.toggleRowSelection) table.toggleRowSelection(a, false)
    }
  }
}
</script>

<style scoped lang="scss">
.asset-library {
  display: flex;
  height: 600px;
  background: #fff;
}

// 2026-10-16 由 220 加宽到 330（用户要求，分组名老被截成省略号）。只加左栏不加弹窗，右侧音频表
// 就会冒横向滚动条（列宽预算见模板里「操作」列的注释），所以两个弹窗壳（AssetManagerDialog /
// AssetPickerDialog）同步 1100→1210，右侧宽度保持原样
.al-left {
  width: 330px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #ebeef5;
  overflow: hidden;
}

.al-kind-tabs {
  flex-shrink: 0;
  padding: 0 12px;

  ::v-deep .el-tabs__header {
    margin-bottom: 4px;
  }

  ::v-deep .el-tabs__active-bar {
    background-color: #6366f1;
  }

  ::v-deep .el-tabs__item {
    // 页签内边距用回 Element 默认的左右 20px。侧栏还是 220px 时，四个页签放不下就退化成带 ‹ ›
    // 箭头的滚动模式、把最后一个页签切掉大半，曾一路压到 6px（2026-09-01 / 09-10）。2026-10-16
    // 侧栏加宽到 330 后不再需要：四个页签文字 42 + 28 + 28 + 56 = 154px（全角汉字每字恒等于字号
    // 14px，换机器也成立），加中间 6 个内侧边各 20px 共 274px，侧栏扣掉左右内边距有 305px。
    // 首尾两个页签的外侧 padding 由 Element 自己的 :nth-child(2) / :last-child 规则清零。
    // 以后再加页签，先跑 asset-kind-tabs.cy.ts 的溢出断言
    &.is-active {
      color: #6366f1;
    }

    &:hover {
      color: #6366f1;
    }
  }
}

.al-right {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.al-toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #ebeef5;
}

.al-search {
  width: 240px;
}

.al-scope-bar,
.al-user-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.al-user-select {
  width: 200px;
}

.al-user-title {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
  white-space: nowrap;
}

.al-manage-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-bottom: 1px solid #ebeef5;
  min-height: 20px;
}

.al-selected-count {
  margin-left: 4px;
  color: #6b7280;
  font-size: 12px;
}

.al-move-select {
  width: 100%;
}

.al-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
}

.al-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 14px;
}

.al-card {
  display: flex;
  flex-direction: column;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  transition: border-color 0.15s ease;

  &.is-pickable {
    cursor: pointer;

    &:hover {
      border-color: #c7d2fe;
    }
  }

  &.is-selected {
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
  }
}

.al-card-thumb {
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 4;
  background: #f5f7fa;
  overflow: hidden;
}

.al-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.al-zoom-btn {
  position: absolute;
  top: 6px;
  left: 6px;
  width: 26px;
  height: 26px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;

  i {
    color: #fff;
    font-size: 14px;
    pointer-events: none;
  }
}

.al-zoom-trigger {
  position: absolute;
  inset: 0;
  opacity: 0;
}

.al-card-check {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 2;

  ::v-deep .el-checkbox {
    pointer-events: none;
  }

  ::v-deep .el-checkbox__inner {
    background: rgba(255, 255, 255, 0.85);
  }
}

.al-card-actions {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  gap: 14px;
  padding: 6px 0;
  background: rgba(0, 0, 0, 0.55);
  opacity: 0;
  transition: opacity 0.15s ease;

  span {
    color: #fff;
    font-size: 12px;
    cursor: pointer;

    &:hover {
      color: #a5b4fc;
    }

    &.is-danger:hover {
      color: #f87171;
    }
  }
}

.al-card:hover .al-card-actions {
  opacity: 1;
}

.al-card-name {
  padding: 6px 8px;
  font-size: 12px;
  color: #374151;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// 本书素材库下卡片名称下面多一行「上传者」，只加高度不影响缩略图/名称行本身的尺寸
.al-card-uploader {
  padding: 0 8px 6px;
  font-size: 11px;
  color: #9ca3af;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.al-audio-name {
  color: #6366f1;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  // manage 模式下官方库非 admin：名称不可点，视觉上不能继续暗示"点了能改名"
  // （2026-09-01 最终审查 I1）
  &.is-readonly {
    color: inherit;
    cursor: default;

    &:hover {
      text-decoration: none;
    }
  }
}

// 同 .al-card-uploader：本书素材库下音频「名称」列里多一行「上传者」，只加行高，不占宽度
.al-audio-uploader {
  font-size: 11px;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.al-audio-player {
  width: 100%;
  height: 32px;
}

// el-table 内部渲染的 <tr>/<td> 不带本组件的 scope 属性，且 Element 自带的行 hover
// 背景（.el-table--enable-row-hover ... tr:hover>td）specificity 比这条高，必须
// !important 才能保证 pick 多选态在 hover 时依然看得出是选中的
::v-deep tr.al-row-selected > td.el-table__cell {
  background-color: rgba(99, 102, 241, 0.12) !important;
}

.al-row-more {
  display: inline-block;
  cursor: pointer;
  color: #909399;
  padding: 4px;

  &:hover {
    color: #6366f1;
  }
}

.al-status-text {
  text-align: center;
  padding: 16px 0;
  color: #9ca3af;
  font-size: 13px;
}

.al-status-end {
  font-size: 12px;
}

.al-confirm-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 10px 16px;
  border-top: 1px solid #ebeef5;
  font-size: 13px;
  color: #4b5563;
}
</style>
